import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// CREW-1286 — the date adapter every picker in the app gets from the root
// LocalizationProvider (DateUtilsProvider).
//
// A pasted date reaches the adapter as `parse(text, 'dd.MM.yyyy')`
// (@mui/x-date-pickers useFieldState.mjs:212), and date-fns wants the
// separators literally — so 21-11-2025 out of a CV's Sea Service table was
// dropped without a word. Here the pasted text is brought to dd.MM.yyyy first:
//
//   - whitespace around it goes: copying a table cell brings a tab or a line break;
//   - day, month and a 4-digit year separated by . - / \ or a space;
//   - ISO, year first: 2025-11-21 — day and month are not swapped.
//
// A two-digit year (21.11.25) is refused rather than widened: a birth date and
// a contract date sit side by side in the same forms, and a guessed century is
// wrong without a word. Before this, date-fns read it as the year 25 AD.
//
// Only the whole-date format is touched. Typing calls `parse` too — sections
// joined by a space with a space-joined format, and single sections such as
// `parse('21', 'dd')` — and those go to date-fns exactly as before.

export const FIELD_FORMAT = 'dd.MM.yyyy';

const DAY_FIRST = /^(\d{1,2})([.\-/\\ ])(\d{1,2})\2(\d{4})$/;
const ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const SHORT_YEAR = /^\d{1,2}([.\-/\\ ])\d{1,2}\1\d{1,3}$/;

// What date-fns cannot turn into a date, whatever the format.
const REFUSED = 'not a date';

const normalise = (text) => {
  const value = text.trim();
  const dayFirst = value.match(DAY_FIRST);
  if (dayFirst) return `${dayFirst[1]}.${dayFirst[3]}.${dayFirst[4]}`;

  const iso = value.match(ISO);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;

  if (SHORT_YEAR.test(value)) return REFUSED;

  return value;
};

// AdapterDateFns defines `parse` as an instance field, not a prototype method,
// so there is no `super.parse` to call: the base's own function is taken from
// the instance and wrapped.
export default class AppDateAdapter extends AdapterDateFns {
  constructor(...args) {
    super(...args);
    const baseParse = this.parse;
    this.parse = (value, format) => (format === FIELD_FORMAT && typeof value === 'string'
      ? baseParse(normalise(value), format)
      : baseParse(value, format));
  }
}

// What MUI X does with a paste into a date field (useFieldSectionContentProps
// .mjs:96-120): letters only or digits only go to the selected section; any
// other text is parsed as the whole date, and a failed parse CLEARS the field.
// So a vessel name pasted by mistake, or a refused 21.11.25, used to wipe a
// date that was there. This names the pastes that would do that, for the
// guard in DateUtilsProvider to stop before MUI sees them.
const SECTION_TEXT = /^([a-zA-Z]+|[0-9]+)$/;
const reader = new AppDateAdapter();

export const isUnreadablePaste = (text) => {
  const value = String(text ?? '');
  if (!value.trim() || SECTION_TEXT.test(value)) return false;
  return !reader.isValid(reader.parse(value, FIELD_FORMAT));
};

